import React, { memo } from 'react'
import { Avatar, Box, Grid } from '@mui/material'
import { faImage } from '@fortawesome/pro-thin-svg-icons'
import { useTheme } from '@mui/material/styles'

import { QUEST_PROFILE_DISPATCH } from '../../store/reducers/quest/quest'
import CustomDialog from '../shared/CustomDialog/CustomDialog'
import FileField from '../shared/inputs/FileField'
import CustomDatePicker from '../shared/inputs/CustomDatePicker'
import {
  transWithArgs,
  validNaturalNumberRule
} from '../../util/validation.helpers'
import Input from '../shared/inputs/Input'
import { LABEL_TEXT, QB_ERROR_TEXT } from '../../constants/messages'
import MultilineField from '../shared/MultilineField/MultilineField'
import {
  NAME_MAX_LENGTH,
  QUEST_AMOUNT_MAX_LENGTH,
  QUEST_DESC_MAX_LENGTH
} from '../../constants/validation'

import { formatTicks } from '../../util/string.helpers'
import CustomFormLabel from '../shared/forms/customElements/CustomFormLabel'
import { CSS_FLEX_CENTERED, CSS_FLEX_COLUMN } from '../../styles/common'
import FaIcon from '../shared/FaIcon/FaIcon'
import { calculatePointValue } from '../../util/token.helpers'
import { DATE_TIME_PICKER_FORMAT } from '../../constants/date'
import { getTodaysDate } from '../../util/date.helpers'

const CreateQuestForm = ({
  questProfile: {
    error,
    isProcessing,
    media,
    mediaUrl,
    titleTemp,
    amountTemp,
    descriptionTemp,
    urlTemp,
    startDateTemp,
    endDateTemp
  },
  tokenProfile: { symbol, onramp },
  dispatchAction,
  onClose,
  onSubmit,
  title,
  submitLabel,
  t
}) => {
  const theme = useTheme()
  return (
    <CustomDialog
      open
      submitProps={{
        label: submitLabel,
        disabled: isProcessing
      }}
      onSubmit={onSubmit}
      onClose={onClose}
      paperProps={{
        maxWidth: '650px'
      }}
      customCss={{
        pt: 0
      }}
      titleCss={{
        pb: 0
      }}
      isForm
      title={title}
      body={
        <>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} sx={CSS_FLEX_COLUMN}>
              <FileField
                id={QUEST_PROFILE_DISPATCH.MEDIA_URL_TEMP}
                accept='image/png, image/jpeg, image/gif'
                label={t('quest.image-format')}
                errorMessage={transWithArgs(
                  t,
                  error[QUEST_PROFILE_DISPATCH.MEDIA_URL_TEMP]
                )}
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    dispatchAction(
                      QUEST_PROFILE_DISPATCH.MEDIA,
                      e.target.files[0]
                    )
                  }
                }}
              />
              <Input
                id={QUEST_PROFILE_DISPATCH.TITLE_TEMP}
                label={t('quest.title')}
                value={titleTemp}
                errorMessage={transWithArgs(
                  t,
                  error[QUEST_PROFILE_DISPATCH.TITLE_TEMP]
                )}
                onChange={(value) =>
                  dispatchAction(QUEST_PROFILE_DISPATCH.TITLE_TEMP, value)
                }
                bottomLabel={t(QB_ERROR_TEXT.CHARACTERS_MAX, {
                  max: formatTicks(NAME_MAX_LENGTH)
                })}
                inputProps={{ maxLength: NAME_MAX_LENGTH }}
                size='small'
              />
              <Input
                id={QUEST_PROFILE_DISPATCH.AMOUNT_TEMP}
                label={t('quest.reward-amount-in', { symbol })}
                value={amountTemp}
                errorMessage={transWithArgs(
                  t,
                  error[QUEST_PROFILE_DISPATCH.AMOUNT_TEMP]
                )}
                onChange={(value) =>
                  dispatchAction(QUEST_PROFILE_DISPATCH.AMOUNT_TEMP, value)
                }
                bottomLabel={t('common.equivalent-to', {
                  value: formatTicks(calculatePointValue(amountTemp, onramp))
                })}
                inputProps={{ maxLength: QUEST_AMOUNT_MAX_LENGTH }}
                size='small'
                validate={[validNaturalNumberRule]}
                placeholder={t('quest.enter-amount')}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={CSS_FLEX_COLUMN}>
              <CustomFormLabel>{t('nft.preview')}</CustomFormLabel>
              {media || mediaUrl ? (
                <Avatar
                  src={media ? URL.createObjectURL(media) : mediaUrl}
                  sx={{ width: 230, height: 230, borderRadius: '7px' }}
                  variant='square'
                />
              ) : (
                <Box
                  sx={{
                    ...CSS_FLEX_CENTERED,
                    width: '230px',
                    height: '230px',
                    border: '1px solid',
                    borderColor: theme.palette.grey.A400,
                    borderRadius: '7px'
                  }}
                >
                  <FaIcon
                    icon={faImage}
                    fontSize={120}
                    color={theme.palette.lightGrey.dark}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MultilineField
                id={QUEST_PROFILE_DISPATCH.DESCRIPTION_TEMP}
                label={t(LABEL_TEXT.DESCRIPTION)}
                value={descriptionTemp}
                errorMessage={transWithArgs(
                  t,
                  error[QUEST_PROFILE_DISPATCH.DESCRIPTION_TEMP]
                )}
                onChange={(value) =>
                  dispatchAction(QUEST_PROFILE_DISPATCH.DESCRIPTION_TEMP, value)
                }
                bottomLabel={t(QB_ERROR_TEXT.CHARACTERS_MAX, {
                  max: formatTicks(QUEST_DESC_MAX_LENGTH)
                })}
                inputProps={{ maxLength: QUEST_DESC_MAX_LENGTH }}
                t={t}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Input
                id={QUEST_PROFILE_DISPATCH.URL_TEMP}
                label={t('quest.link-url')}
                value={urlTemp}
                errorMessage={transWithArgs(
                  t,
                  error[QUEST_PROFILE_DISPATCH.URL_TEMP]
                )}
                onChange={(value) =>
                  dispatchAction(QUEST_PROFILE_DISPATCH.URL_TEMP, value)
                }
                size='small'
                bottomLabel={t('quest.the-quest-link-to-visit')}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <CustomDatePicker
                id={QUEST_PROFILE_DISPATCH.START_DATE_TEMP}
                label={t('quest.quest-start-utc')}
                errorMessage={transWithArgs(
                  t,
                  error[QUEST_PROFILE_DISPATCH.START_DATE_TEMP]
                )}
                onChange={(value) => {
                  dispatchAction(QUEST_PROFILE_DISPATCH.START_DATE_TEMP, value)
                }}
                value={startDateTemp}
                minDateTime={getTodaysDate()}
                size='small'
                enableTime
                inputFormat={DATE_TIME_PICKER_FORMAT}
                inputProps={{
                  placeholder: '',
                  autoComplete: 'off'
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomDatePicker
                id={QUEST_PROFILE_DISPATCH.END_DATE_TEMP}
                label={t('quest.quest-end-utc')}
                errorMessage={transWithArgs(
                  t,
                  error[QUEST_PROFILE_DISPATCH.END_DATE_TEMP]
                )}
                onChange={(value) => {
                  dispatchAction(QUEST_PROFILE_DISPATCH.END_DATE_TEMP, value)
                }}
                value={endDateTemp}
                minDateTime={startDateTemp}
                size='small'
                enableTime
                inputFormat={DATE_TIME_PICKER_FORMAT}
                inputProps={{
                  placeholder: '',
                  autoComplete: 'off'
                }}
              />
            </Grid>
          </Grid>
        </>
      }
      t={t}
    />
  )
}

export default memo(CreateQuestForm)
